import { CircleAlert, CircleCheck, Info, type LucideIcon, TriangleAlert } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";

export function Separator({ className, ...props }: React.ComponentProps<"hr">) {
  return <hr className={cn("m-0 h-px border-0 bg-border", className)} {...props} />;
}

export function Skeleton({ className, ...props }: React.ComponentProps<"div">) {
  return <div aria-hidden className={cn("animate-pulse rounded-md bg-muted", className)} {...props} />;
}

const ALERT: Record<
  "info" | "success" | "warning" | "destructive",
  { icon: LucideIcon; className: string }
> = {
  info: { icon: Info, className: "bg-primary-soft text-foreground [&>svg]:text-primary" },
  success: { icon: CircleCheck, className: "bg-success-soft text-foreground [&>svg]:text-success" },
  warning: { icon: TriangleAlert, className: "bg-warning-soft text-foreground [&>svg]:text-warning" },
  destructive: {
    icon: CircleAlert,
    className: "bg-destructive-soft text-foreground [&>svg]:text-destructive",
  },
};

/** A banner with an icon and words, so the tone is never colour alone. */
export function Alert({
  variant = "info",
  title,
  children,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "title"> & {
  variant?: keyof typeof ALERT;
  title?: React.ReactNode;
}) {
  const a = ALERT[variant];
  const Glyph = a.icon;
  return (
    <div
      data-slot="alert"
      role={variant === "destructive" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-lg px-4 py-3 text-sm", a.className, className)}
      {...props}
    >
      <Glyph aria-hidden strokeWidth={1.75} className="mt-0.5 size-4 shrink-0" />
      <div className="grid gap-0.5">
        {title ? <p className="m-0 font-medium">{title}</p> : null}
        {children ? <div className="text-muted-foreground [&_a]:text-primary [&_a]:underline">{children}</div> : null}
      </div>
    </div>
  );
}

/** Page title row: title (title style), description, actions on the right. */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-end justify-between gap-4", className)}>
      <div className="min-w-0">
        <h1 className="m-0 text-title">{title}</h1>
        {description ? <p className="mt-1 text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Empty state on the drafting grid. */
export function EmptyState({
  icon: Glyph,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid justify-items-center gap-3 rounded-lg border border-dashed border-border bg-drafting-grid px-6 py-12 text-center",
        className,
      )}
    >
      {Glyph ? (
        <span className="grid size-10 place-items-center rounded-lg border border-border bg-card text-muted-foreground shadow-xs">
          <Glyph aria-hidden strokeWidth={1.75} className="size-5" />
        </span>
      ) : null}
      <h2 className="m-0 text-base font-semibold">{title}</h2>
      {description ? <p className="m-0 max-w-md text-body-lg text-muted-foreground">{description}</p> : null}
      {action}
    </div>
  );
}
